import axios from 'axios';
import type { CancelTokenSource } from 'axios';

import type { ServerError } from '../../models';
import type {
    UserGetDailySessionTimeLimitResponse,
    UserGetTradingLimitsResponse,
    UserSetDailySessionTimeLimitRequest,
    UserSetDailySessionTimeLimitResponse,
    UserSetDepositLimitRequest,
    UserSetSelfExclusionRequest,
    UserSetTradingLimitsRequest,
    UserSetTradingLimitsResponse,
    WithdralLimitsResponse,
    WithdrawalSource,
} from '../../models/reqres';
import { buildQueryString } from '../../utils/query';
import { exchangeApi, handleErrorCallback, noop } from '../core';

export function requestSelfExclusion(
    userId: string,
    payload: UserSetSelfExclusionRequest,
    onSuc: () => void,
    onErr: (err: ServerError) => void,
    onFinally: () => void = noop,
): CancelTokenSource {
    const cancelRef = axios.CancelToken.source();
    exchangeApi
        .put(`/users/${userId}/self_exclusion`, payload, { cancelToken: cancelRef.token })
        .then(() => {
            onSuc();
        })
        .catch((error) => {
            handleErrorCallback(error, onErr);
        })
        .finally(() => {
            onFinally();
        });

    return cancelRef;
}

export function requestDepositLimit(
    userId: string,
    payload: UserSetDepositLimitRequest,
    onSuc: () => void,
    onErr: (err: ServerError) => void,
    onFinally: () => void = noop,
): CancelTokenSource {
    const cancelRef = axios.CancelToken.source();
    exchangeApi
        .put(`/users/${userId}/deposit_limit`, payload, { cancelToken: cancelRef.token })
        .then(() => {
            onSuc();
        })
        .catch((error) => {
            handleErrorCallback(error, onErr);
        })
        .finally(() => {
            onFinally();
        });

    return cancelRef;
}

export function requestTradingLimits(
    userId: string,
    payload: UserSetTradingLimitsRequest,
    onSuc: (response: UserSetTradingLimitsResponse) => void,
    onErr: (err: ServerError) => void,
    onFinally: () => void = noop,
): CancelTokenSource {
    const cancelRef = axios.CancelToken.source();
    exchangeApi
        .put<UserSetTradingLimitsResponse>(`/users/${userId}/trading_limits`, payload, {
            cancelToken: cancelRef.token,
        })
        .then((response) => {
            onSuc(response.data);
        })
        .catch((error) => {
            handleErrorCallback(error, onErr);
        })
        .finally(() => {
            onFinally();
        });

    return cancelRef;
}

export function getTradingLimits(
    userId: string,
    onSuc: (response: UserGetTradingLimitsResponse) => void,
    onErr: (err: ServerError) => void = noop,
    onFinally: () => void = noop,
): CancelTokenSource {
    const cancelRef = axios.CancelToken.source();

    exchangeApi
        .get<UserGetTradingLimitsResponse>(`/users/${userId}/trading_limits`, { cancelToken: cancelRef.token })
        .then((response) => {
            onSuc(response.data);
        })
        .catch((error) => {
            handleErrorCallback(error, onErr);
        })
        .finally(() => {
            onFinally();
        });

    return cancelRef;
}

export function requestDailySessionTimeLimit(
    userId: string,
    payload: UserSetDailySessionTimeLimitRequest,
    onSuc: (response: UserSetDailySessionTimeLimitResponse) => void,
    onErr: (err: ServerError) => void,
    onFinally: () => void = noop,
): CancelTokenSource {
    const cancelRef = axios.CancelToken.source();
    exchangeApi
        .put<UserSetDailySessionTimeLimitResponse>(`/users/${userId}/daily_session_time_limit`, payload, {
            cancelToken: cancelRef.token,
        })
        .then((response) => {
            onSuc(response.data);
        })
        .catch((error) => {
            handleErrorCallback(error, onErr);
        })
        .finally(() => {
            onFinally();
        });

    return cancelRef;
}

export function getDailySessionTimeLimit(
    userId: string,
    onSuc: (response: UserGetDailySessionTimeLimitResponse) => void,
    onErr: (err: ServerError) => void = noop,
    onFinally: () => void = noop,
): CancelTokenSource {
    const cancelRef = axios.CancelToken.source();

    exchangeApi
        .get<UserGetDailySessionTimeLimitResponse>(`/users/${userId}/daily_session_time_limit`, {
            cancelToken: cancelRef.token,
        })
        .then((response) => {
            onSuc(response.data);
        })
        .catch((error) => {
            handleErrorCallback(error, onErr);
        })
        .finally(() => {
            onFinally();
        });

    return cancelRef;
}

export function getWithdrawalLimits(
    userId: string,
    source: WithdrawalSource | undefined,
    onSuc: (withdrawalLimits: WithdralLimitsResponse) => void = noop,
    onErr: (err: ServerError) => void = noop,
    onFinally: () => void = noop,
): CancelTokenSource {
    const cancelRef = axios.CancelToken.source();

    exchangeApi
        .get<WithdralLimitsResponse>(`/users/${userId}/withdrawal_times${buildQueryString({ source })}`, {
            cancelToken: cancelRef.token,
        })
        .then((response) => {
            onSuc(response.data);
        })
        .catch((error) => {
            handleErrorCallback(error, onErr);
        })
        .finally(() => {
            onFinally();
        });

    return cancelRef;
}
